







































































































































































































































































import { Component, Mixins, Prop, Inject } from "vue-property-decorator";
import { validate } from "vee-validate";
import { IBasket } from "@/models/baskets";

// Components
import basketItemDetails from "@/components/app/global/baskets/basket/basketView/basketItemDetails.vue";

// Mixins
import Context from "@/mixins/context";

// Enums/Data
import { ProductOrderTypes } from "@/data/enums";
import { QUERY_PARAMS } from "@/data/constants";

// Types
import { IBasketProduct } from "@/models/baskets";
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import { IProductConfig } from "@/models/providers/products";

@Component({
  components: {
    basketItemDetails
  }
})
export default class BasketItem extends Mixins(Context) {
  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly $basketData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  @Inject({ from: "provisionProviderData" })
  public readonly $provisionData!: () => IProvisionProviderData;

  @Inject({ from: "provisionProviderMethods" })
  public readonly $provisionMethods!: IProvisionProviderMethods;

  // Props

  @Prop({ required: true })
  public basketProduct!: IBasketProduct;

  // Data

  public savedBasketId: IBasket["id"] | null = null;

  // Computes

  public get basketData() {
    return this.$basketData();
  }

  public get provisionData() {
    return this.$provisionData();
  }

  public get basket() {
    return this.basketData.basket;
  }

  public get brandTaxType() {
    return this.basketData.brandTaxType;
  }

  public get isVatRegistered() {
    return this.basketData.isVatRegistered;
  }

  public provisioningErrors: number[] = [];

  public showDetails = false;
  public isInProgress = false;
  public deletionLoading = false;
  public quantity = 1;
  public updateQuantityDebounced = this.$_.debounce(this.updateQuantity, 250);

  public async created() {
    this.quantity = this.basketProduct.quantity;
    this.savedBasketId = this.basket.id;

    try {
      if (this.provisionData.hasProvisioning) {
        await this.getBasketProductFields();
      }
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public getBasketProductFields() {
    return this.$provisionMethods.getBasketProductFields(
      this.basket.id,
      this.basketProduct.id,
      this.subProductIds,
      true
    );
  }

  public get basketProductFields() {
    return this.$provisionMethods.basketProductFields(
      this.basketData.basketId,
      this.basketProduct.id
    );
  }

  public get nonQuantityBasedOptions() {
    return this.$_.filter(
      this.basketProduct.options,
      option => option.product.order_type === ProductOrderTypes.SINGLE_OPTION
    );
  }

  public get productImage() {
    return (
      this.$_.get(this.basketProduct, "product.image.full_url", "") ||
      "/assets/images/interface/no-image.svg"
    );
  }

  public get isQuantityBased() {
    return (
      this.basketProduct.product.order_type === ProductOrderTypes.QUANTITY_BASED
    );
  }

  public get subProductIds() {
    return this.$_.map(
      this.$_.concat(this.basketProduct.options, this.basketProduct.attributes),
      (item: any) => item.product_id
    );
  }

  public get hasDetails() {
    return (
      !this.$_.isEmpty(this.basketProduct.options) ||
      !this.$_.isEmpty(this.basketProduct.attributes)
    );
  }

  public get productProvisionErrors() {
    return this.$_.map(
      this.$_.get(this.provisionData.provisionErrors, "data"),
      (errorValue, errorKey) => {
        const productIndex = errorKey.split(".")[1];
        return this.$_.get(this.basket, `products[${productIndex}].id`, "");
      }
    );
  }

  public get quantityValidation() {
    return `min_value:${
      this.basketProduct.product.unit_quantity
    }|divisible_by:${this.basketProduct.product.unit_quantity}${
      this.basketProduct.product.max_order_quantity
        ? `|max_value:${this.basketProduct.product.max_order_quantity}`
        : ""
    }`;
  }

  public async validateQuantity() {
    const result = await validate(this.quantity, this.quantityValidation);

    return result.valid;
  }

  public async updateQuantity() {
    this.updateProduct(async () => {
      if (!(await this.validateQuantity())) {
        this.quantity = this.basketProduct.quantity;
        throw this.$t("_sentence.quantity_is_not_valid");
      }
      this.deletionLoading = true;
      await this.$basketMethods.updateBasketProduct(
        this.basketProduct.id,
        this.computeProductConfig()
      );
    });
  }

  public computeProductConfig() {
    const _ = this.$_;
    return {
      quantity: this.quantity,
      product_id: this.basketProduct.product_id,
      billing_cycle_months: this.basketProduct.billing_cycle_months,
      options: _.map(this.basketProduct.options, option => ({
        product_id: option.product_id,
        billing_cycle_months: option.billing_cycle_months,
        unit_quantity: option.unit_quantity,
        ...(option.selling_price !== option.base_price
          ? {
              selling_price: option.selling_price
            }
          : null)
      })),
      attributes: _.map(this.basketProduct.attributes, attribute => ({
        product_id: attribute.product_id
      })),
      ...(this.basketProduct.selling_price !== this.basketProduct.base_price
        ? {
            selling_price: this.basketProduct.selling_price
          }
        : null)
    } as IProductConfig;
  }

  public async updateProduct(callback: () => {}) {
    if (this.isInProgress) return;
    try {
      this.isInProgress = true;
      await callback();
    } catch (error) {
      this.quantity = this.basketProduct.quantity;
      this.$store.dispatch("api/handleError", error);
      if (
        !this.$store.getters["api/is"](error, 422) &&
        !this.$store.getters["api/is"](error, 404)
      ) {
        return;
      }

      this.$store.dispatch("ui/open/windowModal", {
        config: {
          component: () => import("@/components/app-base/UAlert.vue"),
          width: 480,
          canCancel: [],
          props: {
            title: this.$t("_.unexpected_error") as string,
            message: this.$t(
              "_sentence.something_went_wrong_with_your_basket"
            ) as string,
            confirmText: this.$t("_.refresh_basket") as string
          },
          onCancel: () => this.$basketMethods.getBasket(true)
        }
      });
    } finally {
      this.isInProgress = false;
      this.deletionLoading = false;
    }
  }

  public get hasProvisioningError() {
    return this.productProvisionErrors.includes(this.basketProduct.id);
  }

  public async deleteProduct() {
    if (this.deletionLoading) return;
    this.deletionLoading = true;
    this.updateProduct(async () => {
      await this.$basketMethods.deleteProduct(
        this.basketProduct.id,
        this.basketProduct.invoice_id
      );
      await this.$provisionMethods.checkConfiguration(this.basket.id);
    });
  }

  public get toParams() {
    return {
      name: this.isAdmin ? "adminOrderProductView" : "orderProductViewStep",
      query: {
        [QUERY_PARAMS.BASKET_PRODUCT_ID]: this.basketProduct.id
      }
    };
  }

  public get showEditButton() {
    if (this.isAdmin) {
      return true;
    }

    if (
      this.basketProduct.product.products_options.length > 0 ||
      this.basketProduct.product.products_attributes.length > 0 ||
      (this.provisionData.hasProvisioning &&
        !this.$_.isEmpty(this.basketProductFields))
    ) {
      return true;
    }

    const filteredPrices = this.$_.filter(
      this.basketProduct.product.prices,
      price => {
        return (
          price.currency_id === this.basket.currency_id &&
          price.pricelist_id === this.basket.pricelist_id
        );
      }
    );

    return filteredPrices.length > 1;
  }

  public beforeDestroy() {
    this.$provisionMethods.destroyBasketProductFields(
      this.savedBasketId as IBasket["id"],
      this.basketProduct.id
    );
  }
}
